import InfiniteIcon from "@/assets/colorable/infinite.svg?react";
import RightChevronIcon from "@/assets/colorable/right_chevron.svg?react";
import InstagramIcon from "@/assets/colorable/unverified_instagram.svg?react";
import TiktokIcon from "@/assets/colorable/unverified_tiktok.svg?react";
import UGCIcon from "@/assets/colorable/unverified_ugc.svg?react";
import DisciplinaryIcon from "@/assets/dark_gradient/hammer.svg?react";
import Button from "@/components/Common/Button";
import Modal from "@/components/Common/Modal";
import DisciplineMark from "@/components/discipline/DisciplineMark";
import DisciplineTabs from "@/components/discipline/DisciplineTabs";
import DisciplineTemplateForm from "@/components/discipline/DisciplineTemplateForm";
import FlaggedCampaign from "@/components/discipline/FlaggedCampaign";
import ModalButtonRow from "@/components/discipline/ModalButtonRow";
import RestrictionSection from "@/components/discipline/RestrictionSection";
import {useDiscipline} from "@/hooks/useDiscipline";
import {useIsAdmin} from "@/hooks/useUserHooks";
import {FULL_DATE_FORMAT} from "@/store/constants";
import {
  disciplinaryModalAtom,
  disciplineTabAtom,
  isDisciplinaryModalOpenAtom,
  isSingleDisciplineModalOpenAtom,
  isTriggeredFromDisciplinaryModalAtom,
} from "@/store/jotaiAtoms";
import {isDisciplineOngoing, isUnverifiedType} from "@/utils/disciplineUtils";
import dayjs from "dayjs";
import {useAtom, useAtomValue, useSetAtom} from "jotai";
import {memo, useCallback, useEffect, useMemo, type FC} from "react";
import {useTranslation} from "react-i18next";
import {usePrevious, useUnmount} from "react-use";

const SingleDisciplineModal: FC = () => {
  const disciplinaryData = useAtomValue(disciplinaryModalAtom);
  const setModalOpen = useSetAtom(isSingleDisciplineModalOpenAtom);
  const setDisciplinaryModalOpen = useSetAtom(isDisciplinaryModalOpenAtom);
  const [disciplineTab, setDisciplineTab] = useAtom(disciplineTabAtom);
  const [isTriggeredFromDisciplinaryModal, setTriggeredFromDisciplinaryModal] = useAtom(
    isTriggeredFromDisciplinaryModalAtom,
  );
  const {getLabelByDisciplineType, getDisciplinaryModalPreviousLocation} = useDiscipline();
  const {t} = useTranslation("influencer_discipline");
  const previousTab = usePrevious(disciplineTab);
  const {selectedDiscipline} = disciplinaryData;
  const isAdmin = useIsAdmin();

  const platformData = useMemo(
    () => ({
      UN_VERIFIED_INSTAGRAM: {
        Icon: InstagramIcon,
        title: "instagram",
      },
      UN_VERIFIED_TIKTOK: {
        Icon: TiktokIcon,
        title: "tiktok",
      },
      UN_VERIFIED_UGC: {
        Icon: UGCIcon,
        title: "ugc",
      },
    }),
    [],
  );

  const UnverificationIcon = useMemo(
    () =>
      selectedDiscipline?.punishment_type && isUnverifiedType(selectedDiscipline.punishment_type)
        ? platformData[selectedDiscipline.punishment_type].Icon
        : null,
    [platformData, selectedDiscipline?.punishment_type],
  );

  useEffect(() => {
    if (selectedDiscipline?.punishment_type) {
      setDisciplineTab(
        isUnverifiedType(selectedDiscipline.punishment_type) ? "UNVERIFY" : selectedDiscipline.punishment_type,
      );
    }
  }, [selectedDiscipline?.punishment_type, setDisciplineTab]);

  useUnmount(() => {
    setDisciplineTab(previousTab ?? "UNVERIFY");
    setTriggeredFromDisciplinaryModal(false);
  });

  const openMainDisciplineModal = useCallback(() => {
    setDisciplinaryModalOpen(true);
    setModalOpen(false);
  }, [setDisciplinaryModalOpen, setModalOpen]);

  if (!selectedDiscipline || !isAdmin) {
    return null;
  }

  return (
    <Modal
      backgroundClassName="max-w-[830px] bg-white"
      className="gap-0 p-0 pt-5"
      customBottomRow={
        <ModalButtonRow
          hideTemplateButton
          onBack={openMainDisciplineModal}
          onSuccessAction={() => setModalOpen(false)}
        />
      }
      customTitleRow={
        <div className="flex items-center gap-3 pt-5 pb-4 pl-5 max-md:flex-col">
          <div className="flex gap-3">
            <Button
              buttonStyle="White"
              className="h-fit gap-2 px-4 py-2 max-md:px-2"
              onClick={() => setModalOpen(false)}>
              <RightChevronIcon className="text-primary-dark-solid size-3 rotate-180" />
              <span className="text-sm font-medium max-md:text-xs">{getDisciplinaryModalPreviousLocation()}</span>
            </Button>
            {isTriggeredFromDisciplinaryModal && (
              <Button
                buttonStyle="White"
                className="h-fit gap-2 px-4 py-2 max-md:px-2"
                onClick={openMainDisciplineModal}>
                <RightChevronIcon className="text-primary-dark-solid size-3 rotate-180" />
                {<span className="text-sm font-medium text-nowrap max-md:text-xs">{t("disciplinary_action")}</span>}
              </Button>
            )}
          </div>
          <div className="flex items-center gap-2">
            <DisciplinaryIcon className="size-4" />
            <span className="text-neutral-black text-xl font-semibold">
              {`${getLabelByDisciplineType(selectedDiscipline.punishment_type)} ${isDisciplineOngoing(selectedDiscipline) ? "" : dayjs(selectedDiscipline.expiration_date).format(FULL_DATE_FORMAT)}`}
            </span>
          </div>
        </div>
      }
      onClose={() => setModalOpen(false)}>
      <DisciplineTabs
        tabs={[
          isUnverifiedType(selectedDiscipline.punishment_type) && UnverificationIcon
            ? {
                title: t("platform_unverified", {
                  platform: t(`common:${platformData[selectedDiscipline.punishment_type].title}`),
                }),
                Icon: <UnverificationIcon className="text-danger-raspberry" />,
              }
            : {
                title: getLabelByDisciplineType(selectedDiscipline.punishment_type),
                Icon: (
                  <DisciplineMark className="size-3.5" type={selectedDiscipline.punishment_type} variant="outline" />
                ),
              },
        ]}
      />
      <div className="flex flex-col gap-6 px-5 py-6">
        {selectedDiscipline.campaign_application && typeof selectedDiscipline.campaign_application !== "number" && (
          <FlaggedCampaign data={selectedDiscipline.campaign_application} />
        )}
        <div className="flex gap-2">
          <div className="mr-2 flex flex-col gap-2">
            <div className="text-neutral-black flex flex-col gap-2">
              <span className="text-sm">{t("created_date")}</span>
              <span className="font-semibold">{dayjs(selectedDiscipline.created).format(FULL_DATE_FORMAT)}</span>
            </div>
          </div>
          <div className="flex flex-col gap-2">
            <div className="text-neutral-black flex flex-col gap-2">
              <span className="text-sm">{t("start_date")}</span>
              <span className="font-semibold">{dayjs(selectedDiscipline.effective_date).format(FULL_DATE_FORMAT)}</span>
            </div>
          </div>
          <span className="flex items-end font-bold">-</span>
          <div className="flex flex-col gap-2">
            <div className="text-neutral-black flex flex-col gap-2">
              <span className="line-clamp-1 text-sm">
                {t(dayjs().isBefore(dayjs(selectedDiscipline.expiration_date)) ? "finishing_date" : "date_finished")}
              </span>
              <span className="font-semibold">
                {isDisciplineOngoing(selectedDiscipline) ? (
                  <InfiniteIcon className="size-5" />
                ) : (
                  dayjs(selectedDiscipline.expiration_date).format(FULL_DATE_FORMAT)
                )}
              </span>
            </div>
          </div>
        </div>
        <RestrictionSection />
        <DisciplineTemplateForm selectedPunishmentType={selectedDiscipline.punishment_type} hideFormConfig />
      </div>
    </Modal>
  );
};

export default memo(SingleDisciplineModal);
